import { createPortal } from 'react-dom';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuthStore } from '@/store/auth';
import { useT } from '@/i18n';

export const CHANGE_PASSWORD_PATH = '/mine/settings/change-password';

/**
 * Whether the blocking prompt shows for a given flag and route.
 *
 * Extracted as a pure function so the rule can be tested: the repo's test setup
 * runs plain .ts files with no DOM, and this decision — not the markup — is
 * where the reported bug lived. Reaching the change-password screen does not
 * clear the flag, so leaving it must block again.
 */
export function shouldShowWeakPasswordPrompt(passwordWeak: boolean, pathname: string): boolean {
  if (!passwordWeak) return false;
  // startsWith, not equality: a trailing slash or any nested path under the
  // change-password screen still counts as being on it. Equality would put the
  // blocking overlay back over the very page it sent the user to.
  return !pathname.startsWith(CHANGE_PASSWORD_PATH);
}

/**
 * Blocks the app when the server reports the signed-in password is guess-first.
 *
 * There is deliberately no close affordance: no cross, no backdrop click, no
 * Escape handler. Two ways out, both of which resolve the situation rather than
 * dismiss it — change the password, or sign out. Neither leaves a signed-in
 * session sitting behind a dismissed warning.
 *
 * It hides itself while the user is ON that form — otherwise the prompt would
 * cover the page it sent them to — but it does NOT clear the flag there.
 * Navigating away without changing brings it straight back, which is what makes
 * "cannot do anything else" true rather than a suggestion. The flag is cleared
 * only by a successful password change.
 *
 * Mounted at the app root rather than on the login page because the user is
 * navigated away the moment they sign in; a prompt owned by that screen would
 * unmount with it.
 */
export function WeakPasswordPopup() {
  const t = useT('common');
  const navigate = useNavigate();
  const location = useLocation();
  const passwordWeak = useAuthStore((s) => s.passwordWeak);

  const shouldShow = shouldShowWeakPasswordPrompt(passwordWeak, location.pathname);

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!shouldShow) {
      setVisible(false);
      return;
    }
    // One frame before fading in, matching the app's other blocking popup.
    const id = setTimeout(() => setVisible(true), 16);
    return () => clearTimeout(id);
  }, [shouldShow]);

  if (!shouldShow) return null;

  return createPortal(
    <>
      {/*
        aria-hidden and no onClick: the backdrop is scenery, not a dismiss
        target. Clicking it must do nothing.
      */}
      <div
        aria-hidden
        className="fixed inset-0 transition-opacity duration-300"
        style={{ zIndex: 9998, opacity: visible ? 1 : 0, backgroundColor: 'rgba(0,0,0,0.5)' }}
      />

      <div
        role="alertdialog"
        aria-modal
        aria-labelledby="weak-password-title"
        className="fixed inset-x-0 top-1/2 mx-auto w-[calc(100%-2.5rem)] max-w-[22rem] overflow-hidden rounded-[1.25rem] bg-white shadow-2xl transition-all duration-300"
        style={{
          zIndex: 9999,
          opacity: visible ? 1 : 0,
          transform: `translateY(calc(-50% + ${visible ? '0px' : '12px'}))`,
        }}
      >
        <div className="px-6 pb-6 pt-5">
          <h2
            id="weak-password-title"
            className="text-center text-[1rem] font-bold leading-snug text-black"
          >
            {t('weak_password_title')}
          </h2>

          <p className="mt-2 text-center text-[0.8125rem] leading-[1.5] text-black/60">
            {t('weak_password_body')}
          </p>

          <button
            type="button"
            onClick={() => navigate(CHANGE_PASSWORD_PATH)}
            className="mt-5 flex h-11 w-full items-center justify-center rounded-full bg-brand text-[0.875rem] font-semibold text-white"
          >
            {t('weak_password_action')}
          </button>

          {/*
            Signing out clears the token AND the flag — a logged-out user has no
            password to be prompted about. replace: true so the back gesture
            cannot return to the signed-in screen the prompt was covering.
          */}
          <button
            type="button"
            onClick={() => {
              useAuthStore.getState().clear();
              navigate('/', { replace: true });
            }}
            className="mt-2.5 flex h-11 w-full items-center justify-center rounded-full bg-black/[0.04] text-[0.875rem] font-semibold text-black/60"
          >
            {t('weak_password_logout')}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
